// Step 1: BrandApplication1 for brand signup wizard
// Collects company information: Company Name, Logo (optional), and Company Bio
// Simplified 2-step wizard - Step 1 of 2

import React, { useState, useEffect } from 'react';

const BrandApplication1 = ({ data, onDataChange, onNext, user }) => {
  // Editable copy constants
  const COPY = {
    title: 'Company Information',
    subtitle: 'Tell us about your company!',
    continueButton: 'Continue'
  };

  const [formData, setFormData] = useState({
    brand: {
      companyName: data?.brand?.companyName || '',
      companyWebsite: data?.brand?.companyWebsite || '',
      description: data?.brand?.description || '' // Company Bio
    },
    logoFile: data?.logoFile || null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [logoPreview, setLogoPreview] = useState(null);

  // Update parent component when form data changes
  useEffect(() => {
    onDataChange(formData);
  }, [formData, onDataChange]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested fields
    if (name.startsWith('brand.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        brand: {
          ...prev.brand,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0] || null;
    
    if (!file) {
      setFormData(prev => ({
        ...prev,
        logoFile: null
      }));
      setLogoPreview(null);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    // Store file in form data
    setFormData(prev => ({
      ...prev,
      logoFile: file
    }));

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Validation function
  const validate = async (stepData) => {
    console.log('🔍 BrandApplication1 validation called with data:', stepData);
    const errors = [];
    const brand = stepData.brand || {};
    
    // Brand validation
    if (!brand.companyName?.trim()) {
      errors.push('Company name is required');
    }
    
    // Company Website validation (required)
    if (!brand.companyWebsite?.trim()) {
      errors.push('Company website is required');
    }
    
    // Company Bio validation (required, min 50 characters)
    if (!brand.description?.trim()) {
      errors.push('Company bio is required');
    } else if (brand.description.trim().length < 50) {
      errors.push('Company bio must be at least 50 characters');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  };

  // Attach validate function to component
  BrandApplication1.validate = validate;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const validation = await validate(formData);
    if (!validation.valid) {
      setError(validation.errors.join(', '));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Update wizard state with Step 1 data
      const step1Data = {
        brand: formData.brand,
        logoFile: formData.logoFile
      };
      onDataChange(step1Data);

      // Advance to next step
      onNext();

    } catch (error) {
      console.error('Brand setup error:', error);
      setError('Failed to process step. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Error Display */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
          {error}
        </div>
      )}

      {/* Company Information Section */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Company Information</h3>
        
        <div className="space-y-6">
          {/* Company Name */}
          <div>
            <label htmlFor="brand.companyName" className="form-label">
              Company Name *
            </label>
            <input
              id="brand.companyName"
              name="brand.companyName"
              type="text"
              value={formData.brand.companyName}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter your company name"
              required
            />
          </div>

          {/* Company Website */}
          <div>
            <label htmlFor="brand.companyWebsite" className="form-label">
              Company Website *
            </label>
            <input
              id="brand.companyWebsite"
              name="brand.companyWebsite"
              type="text"
              value={formData.brand.companyWebsite}
              onChange={handleChange}
              className="form-input"
              placeholder="https://yourcompany.com"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Enter your company website URL
            </p>
          </div>

          {/* Company Logo (Optional) */}
          <div>
            <label htmlFor="company-logo" className="block text-sm font-medium text-gray-700 mb-2">
              Company Logo <span className="text-gray-400 text-xs">(optional)</span>
            </label>
            {logoPreview && (
              <div className="mb-2">
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="h-16 w-16 rounded-full object-cover border border-gray-300"
                />
              </div>
            )}
            <input
              id="company-logo"
              type="file"
              accept="image/*"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-600
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
            />
            <p className="mt-1 text-xs text-gray-400">
              Upload your brand logo so influencers can recognize you. Max 5MB. JPG, PNG, or GIF.
            </p>
          </div>

          {/* Company Bio */}
          <div>
            <label htmlFor="brand.description" className="form-label">
              Company Bio *
            </label>
            <textarea
              id="brand.description"
              name="brand.description"
              rows={6}
              value={formData.brand.description}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Describe your company, what you do, your products/services, and what makes you unique. This will help influencers understand your brand better."
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Minimum 50 characters. This description will be visible to potential influencer partners.
            </p>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="pt-4">
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {isSubmitting ? 'Processing...' : COPY.continueButton}
        </button>
      </div>
    </form>
  );
};

export default BrandApplication1;
